/**
 * Wayfinding Utilities
 *
 * A* pathfinding algorithm for routing through path networks
 * with turn-by-turn instruction generation.
 */

import type { PathNetwork, PathNode, RouteOverlay } from "./pathNetwork";
import {
  getConnections,
  getEdge,
  getNode,
} from "./pathNetwork";
import { buildRouteOverlay } from "./snapDispatch";
import { calcDistance, calcBearing, coordToId } from "./spatial";

/** Walking speed in meters per second */
const WALKING_SPEED = 1.4;

/** Accessible walking speed in meters per second (wheelchair/stroller) */
const ACCESSIBLE_WALKING_SPEED = 1.0;

/** Routing constraint options */
export interface RoutingOptions {
  avoidStairs?: boolean;
  avoidSteepGrade?: boolean;
}

/** Minimum angle change (degrees) to consider a turn */
const TURN_THRESHOLD = 30;

/** Direction types for i18n-safe turn instructions */
export const DirectionType = {
  continueStraight: "continueStraight",
  slightRight: "slightRight",
  right: "right",
  sharpRight: "sharpRight",
  slightLeft: "slightLeft",
  left: "left",
  sharpLeft: "sharpLeft",
  start: "start",
  arrive: "arrive",
} as const;
export type DirectionType = (typeof DirectionType)[keyof typeof DirectionType];

/** Fallback English labels for direction types (used when i18n unavailable) */
const DIRECTION_LABELS: Record<DirectionType, string> = {
  continueStraight: "Continue straight",
  slightRight: "Turn slight right",
  right: "Turn right",
  sharpRight: "Turn sharp right",
  slightLeft: "Turn slight left",
  left: "Turn left",
  sharpLeft: "Turn sharp left",
  start: "Start walking",
  arrive: "You have arrived",
};

/** Instruction type for route guidance */
export interface RouteInstruction {
  type: "start" | "turn" | "arrive";
  directionType: DirectionType;
  text: string;
  distance: number;
  /** Landmark name at turn point (if available) */
  landmark?: string;
  /** Index into the route path array where this instruction occurs */
  pathIndex: number;
}

/** Complete route result */
export interface Route {
  path: [number, number][];
  totalDistance: number;
  estimatedTime: number;
  instructions: RouteInstruction[];
  hasStairs: boolean;
  hasSteepGrade: boolean;
  /** Route-scoped overlay from edge-snap dispatch (Plan 121). Exposed so
   *  diagnostic helpers (`labelRoutePath`) can resolve virtual edges that
   *  don't exist in `network.edges`. Production logic should not branch on
   *  this — it's purely for debug surfacing. Always present in routes
   *  produced by `findRoute`. */
  overlay?: RouteOverlay;
}


/**
 * Get turn direction type based on angle change
 */
function getTurnDirection(angleDiff: number): DirectionType {
  // Normalize to -180 to 180
  while (angleDiff > 180) angleDiff -= 360;
  while (angleDiff < -180) angleDiff += 360;

  if (Math.abs(angleDiff) < TURN_THRESHOLD) {
    return DirectionType.continueStraight;
  } else if (angleDiff > 0 && angleDiff < 90) {
    return DirectionType.slightRight;
  } else if (angleDiff >= 90 && angleDiff < 150) {
    return DirectionType.right;
  } else if (angleDiff >= 150) {
    return DirectionType.sharpRight;
  } else if (angleDiff < 0 && angleDiff > -90) {
    return DirectionType.slightLeft;
  } else if (angleDiff <= -90 && angleDiff > -150) {
    return DirectionType.left;
  } else {
    return DirectionType.sharpLeft;
  }
}

/**
 * A* heuristic: straight-line distance to goal
 */
function heuristic(node: PathNode, goal: PathNode): number {
  return calcDistance(node.coords, goal.coords);
}

/**
 * Get edge cost between two connected nodes, applying routing constraints.
 *
 * Overlay-aware: reads virtual edges from the overlay first (Plan 121).
 * `getEdge` handles the priority — overlay wins.
 */
function getEdgeCost(
  network: PathNetwork,
  overlay: RouteOverlay,
  fromId: string,
  toId: string,
  options?: RoutingOptions
): number {
  const edge = getEdge(network, overlay, fromId, toId);
  if (!edge) return Infinity;

  if (options?.avoidStairs && edge.metadata?.hasStairs) return Infinity;

  let cost = edge.distance;
  const weight = edge.metadata?.wayfindingWeight ?? 1.0;
  cost *= weight;
  if (options?.avoidSteepGrade && edge.metadata?.steepGrade) cost *= 3;

  return cost;
}

/**
 * Reconstruct path from A* came-from map. Overlay-aware: virtual nodes
 * resolve their coords from the overlay (Plan 121).
 */
function reconstructPath(
  cameFrom: Map<string, string>,
  current: string,
  network: PathNetwork,
  overlay: RouteOverlay
): [number, number][] {
  const path: [number, number][] = [];
  let currentId: string | undefined = current;

  while (currentId) {
    const node = getNode(network, overlay, currentId);
    if (node) {
      path.unshift(node.coords);
    }
    currentId = cameFrom.get(currentId);
  }

  return path;
}

/**
 * Truncate text to max length with ellipsis
 */
function truncateName(name: string, maxLength: number = 25): string {
  if (name.length <= maxLength) return name;
  return name.slice(0, maxLength - 1) + "…";
}

/**
 * Generate turn-by-turn instructions from a path
 *
 * @param path - Array of coordinates representing the route
 * @param pois - Optional array of POIs for landmark-based instructions
 */
function generateInstructions(
  path: [number, number][],
  pois?: LandmarkPOI[]
): RouteInstruction[] {
  if (path.length < 2) return [];

  const instructions: RouteInstruction[] = [];

  // Start instruction
  instructions.push({
    type: "start",
    directionType: DirectionType.start,
    text: "Start walking",
    distance: 0,
    pathIndex: 0,
  });

  let prevBearing: number | null = null;
  let lastLandmarkId: string | null = null;

  for (let i = 0; i < path.length - 1; i++) {
    const from = path[i];
    const to = path[i + 1];
    const segmentDist = calcDistance(from, to);
    const bearing = calcBearing(from, to);

    if (prevBearing !== null) {
      const angleDiff = bearing - prevBearing;
      const dirType = getTurnDirection(angleDiff);

      if (Math.abs(angleDiff) >= TURN_THRESHOLD) {
        const nearbyLandmark = pois ? findNearbyLandmark(from, pois) : null;
        const useLandmark = nearbyLandmark && nearbyLandmark.id !== lastLandmarkId;

        // Fallback English text (used when i18n not available)
        let text: string;
        let landmarkName: string | undefined;
        if (useLandmark) {
          landmarkName = truncateName(nearbyLandmark.name);
          const cap = DIRECTION_LABELS[dirType];
          text = `${cap} at ${landmarkName}`;
          lastLandmarkId = nearbyLandmark.id;
        } else {
          text = `In ${Math.round(segmentDist)}m, ${DIRECTION_LABELS[dirType].toLowerCase()}`;
        }

        instructions.push({
          type: "turn",
          directionType: dirType,
          text,
          distance: segmentDist,
          landmark: landmarkName,
          pathIndex: i,
        });
      }
    }

    prevBearing = bearing;
  }

  // Arrive instruction
  const totalDist = path.reduce((acc, coord, i) => {
    if (i === 0) return 0;
    return acc + calcDistance(path[i - 1], coord);
  }, 0);

  instructions.push({
    type: "arrive",
    directionType: DirectionType.arrive,
    text: "You have arrived at your destination",
    distance: totalDist,
    pathIndex: path.length - 1,
  });

  return instructions;
}

/**
 * Find a route through the path network using A* algorithm
 *
 * @param network - The path network to route through
 * @param start - Starting coordinates [lng, lat]
 * @param end - Ending coordinates [lng, lat]
 * @param pois - Optional array of POIs for landmark-based instructions
 * @returns Route with path, distance, time, and instructions, or null if no path exists
 */
export function findRoute(
  network: PathNetwork,
  start: [number, number],
  end: [number, number],
  pois?: LandmarkPOI[],
  options?: RoutingOptions
): Route | null {
  // Plan 121: edge-snap dispatch builds a route-scoped overlay that may inject
  // virtual nodes at start/end snap points. Original network stays immutable.
  const dispatch = buildRouteOverlay(network, start, end);
  if (!dispatch) return null;
  const { overlay, startNodeId, endNodeId } = dispatch;

  const startNode = getNode(network, overlay, startNodeId);
  const endNode = getNode(network, overlay, endNodeId);
  if (!startNode || !endNode) return null;

  // Same-node trivial case (both clicks fast-pathed to the same existing node).
  if (startNodeId === endNodeId) {
    return {
      path: [start, startNode.coords, end],
      totalDistance: calcDistance(start, startNode.coords) + calcDistance(startNode.coords, end),
      estimatedTime: 0,
      instructions: [
        { type: "start", directionType: DirectionType.start, text: "Start walking", distance: 0, pathIndex: 0 },
        { type: "arrive", directionType: DirectionType.arrive, text: "You have arrived at your destination", distance: 0, pathIndex: 2 },
      ],
      hasStairs: false,
      hasSteepGrade: false,
      overlay,
    };
  }

  // A* over network + overlay via accessor helpers. No mutation of either.
  const openSet = new Set<string>([startNodeId]);
  const cameFrom = new Map<string, string>();
  const gScore = new Map<string, number>();
  gScore.set(startNodeId, 0);
  const fScore = new Map<string, number>();
  fScore.set(startNodeId, heuristic(startNode, endNode));

  while (openSet.size > 0) {
    // Find node in openSet with lowest fScore.
    let current: string | null = null;
    let lowestF = Infinity;
    for (const nodeId of openSet) {
      const f = fScore.get(nodeId) ?? Infinity;
      if (f < lowestF) {
        lowestF = f;
        current = nodeId;
      }
    }
    if (!current) break;

    // Reached goal — reconstruct and shape the output path.
    if (current === endNodeId) {
      const networkPath = reconstructPath(cameFrom, current, network, overlay);

      // Prefix the actual click point if it's not already the first coord
      // (within COORD_TOLERANCE, since coordToId equality is canonical).
      const fullPath: [number, number][] = [];
      if (coordToId(start) !== coordToId(networkPath[0])) {
        fullPath.push(start);
      }
      fullPath.push(...networkPath);
      if (coordToId(end) !== coordToId(networkPath[networkPath.length - 1])) {
        fullPath.push(end);
      }

      let totalDistance = 0;
      for (let i = 0; i < fullPath.length - 1; i++) {
        totalDistance += calcDistance(fullPath[i], fullPath[i + 1]);
      }

      // Scan edges (overlay-aware) for accessibility flags.
      let hasStairs = false;
      let hasSteepGrade = false;
      const pathNodeIds = networkPath.map((coord) => coordToId(coord));
      for (let i = 0; i < pathNodeIds.length - 1; i++) {
        const edge = getEdge(network, overlay, pathNodeIds[i], pathNodeIds[i + 1]);
        if (edge?.metadata?.hasStairs) hasStairs = true;
        if (edge?.metadata?.steepGrade) hasSteepGrade = true;
      }

      const instructions = generateInstructions(fullPath, pois);
      const speed = options?.avoidStairs ? ACCESSIBLE_WALKING_SPEED : WALKING_SPEED;
      const estimatedTime = totalDistance / speed;

      return {
        path: fullPath,
        totalDistance,
        estimatedTime,
        instructions,
        hasStairs,
        hasSteepGrade,
        overlay,
      };
    }

    openSet.delete(current);
    const currentNode = getNode(network, overlay, current);
    if (!currentNode) continue;

    for (const neighborId of getConnections(network, overlay, current)) {
      const neighborNode = getNode(network, overlay, neighborId);
      if (!neighborNode) continue;

      const edgeDist = getEdgeCost(network, overlay, current, neighborId, options);
      const tentativeG = (gScore.get(current) ?? Infinity) + edgeDist;

      if (tentativeG < (gScore.get(neighborId) ?? Infinity)) {
        cameFrom.set(neighborId, current);
        gScore.set(neighborId, tentativeG);
        fScore.set(neighborId, tentativeG + heuristic(neighborNode, endNode));
        if (!openSet.has(neighborId)) openSet.add(neighborId);
      }
    }
  }

  return null;
}

/** POI for landmark-based instructions */
export interface LandmarkPOI {
  id: string;
  name: string;
  coordinates: [number, number];
}

/** Default max distance in meters for nearby landmark search */
const DEFAULT_LANDMARK_DISTANCE = 30;

/**
 * Find the closest POI within a given distance of a point
 *
 * @param point - Coordinates [lng, lat] to search from
 * @param pois - Array of POIs to search through
 * @param maxDistance - Maximum distance in meters (default: 30)
 * @returns Closest POI within maxDistance, or null if none found
 */
export function findNearbyLandmark(
  searchPoint: [number, number],
  pois: LandmarkPOI[],
  maxDistance: number = DEFAULT_LANDMARK_DISTANCE
): { id: string; name: string } | null {
  let closestPOI: { id: string; name: string } | null = null;
  let closestDistance = Infinity;

  for (const poi of pois) {
    const dist = calcDistance(searchPoint, poi.coordinates);

    if (dist <= maxDistance && dist < closestDistance) {
      closestDistance = dist;
      closestPOI = { id: poi.id, name: poi.name };
    }
  }

  return closestPOI;
}

/**
 * Determine which instruction the user should see based on their progress along the route.
 * Returns the index of the next upcoming instruction.
 */
export function getCurrentInstructionIndex(
  instructions: RouteInstruction[],
  completedSegmentIndex: number
): number {
  for (let i = instructions.length - 1; i >= 0; i--) {
    if (instructions[i].pathIndex <= completedSegmentIndex) {
      return Math.min(i + 1, instructions.length - 1);
    }
  }
  return 0;
}
